/**
 * The pack's chart appendices, chosen and ordered in Admin: the cash
 * forecast, the measures (each with its months), objectives' progress, the
 * risks on the quadrant, and statutory changes against their targets. Each is
 * worked out as data, frozen into the issued pack with everything else, and
 * drawn from that data on screen and in the PDF.
 */
import { addDays, dayLabel, type ISODate } from "./dates";
import { engineInput } from "./model";
import { forecast, gateState, type GateState } from "./engine";
import type { FinanceModel } from "./types";
import { SOURCE, isLive, measureSeries, type MeasureDef, type MeasureStatus, type MeasureUnit, type SeriesPoint } from "./measures";
import { RELEASE_OPEN, releaseRef, releaseTiming } from "./releases";
import { bandOf, riskRef, score, type RiskRating, type RiskScale } from "./risks";
import type { RegistersModel } from "./registers";

export type AppendixKey = "cash" | "measures" | "objectives" | "risks" | "releases";
export const APPENDIX_LABEL: Record<AppendixKey, string> = {
  cash: "Cash forecast",
  measures: "Measures",
  objectives: "Objectives progress",
  risks: "Risks",
  releases: "Statutory changes",
};
export const APPENDIX_KEYS = Object.keys(APPENDIX_LABEL) as AppendixKey[];

export interface CashAppendix {
  kind: "cash";
  title: string;
  asOf: ISODate;
  /** Contracted closes, pence. */
  weeks: { start: ISODate; close: number; state: GateState }[];
  thresholds: { green: number; amber: number; red: number };
  gateWeeks: number;
  low: { start: ISODate; close: number };
  firstOverdrawn: ISODate | null;
}

export interface MeasureChart {
  id: string;
  name: string;
  unit: MeasureUnit;
  better: MeasureDef["better"];
  headline: boolean;
  owner: string | null;
  source: string;
  /** Its months in the window, from the live-from month on. */
  points: SeriesPoint[];
}
export interface MeasuresAppendix {
  kind: "measures";
  title: string;
  measures: MeasureChart[];
  /** Live in the window, but no figures yet. */
  noFigures?: string[];
}

export interface ObjectiveBar {
  title: string;
  owner: string | null;
  periodStart: string;
  periodEnd: string;
  /** 0–1 (can pass 1). */
  progress: number;
  /** The share of the period gone, 0–1. */
  gone: number;
  status: MeasureStatus;
  keyResult: string | null;
  /** The Sales Hub's own reading, where it differs. */
  hubSays: string | null;
}
export interface ObjectivesAppendix {
  kind: "objectives";
  title: string;
  items: ObjectiveBar[];
}

export interface RiskPoint {
  id: string;
  ref: string;
  statement: string;
  residual: RiskRating;
  inherent: RiskRating | null;
  score: number;
  band: string;
  owner: string | null;
}
export interface RisksAppendix {
  kind: "risks";
  title: string;
  scale: RiskScale;
  points: RiskPoint[];
}

export interface ChangeBar {
  ref: string;
  what: string;
  framework: string;
  publishedOn: ISODate | null;
  toDetect: number | null;
  detectTarget: number | null;
  toDeploy: number | null;
  deployTarget: number | null;
  /** Still counting. */
  open: boolean;
  overDetect: boolean;
  overDeploy: boolean;
  unsigned: boolean;
}
export interface ReleasesAppendix {
  kind: "releases";
  title: string;
  items: ChangeBar[];
}

export type Appendix = CashAppendix | MeasuresAppendix | ObjectivesAppendix | RisksAppendix | ReleasesAppendix;

const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const full = (month: string) => `${MONTH_FULL[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

/** The `count` months ending with `month`. */
export function monthsTo(month: string, count: number): string[] {
  const out = [month];
  while (out.length < count) out.unshift(prevOf(out[0]!));
  return out;
}
const prevOf = (m: string) => addDays(`${m}-01`, -1).slice(0, 7);

/** Progress against the share of the period gone (Maggie's 6): on track within 5 points, at risk within 15. */
export function paceOf(progress: number, start: string, end: string, today: ISODate): { gone: number; status: MeasureStatus } {
  const span = Math.max(1, (Date.parse(end) - Date.parse(start)) / 86400000);
  const gone = Math.min(1, Math.max(0, (Date.parse(today) - Date.parse(start)) / 86400000 / span));
  const d = (progress - gone) * 100;
  return { gone, status: d >= -5 ? "on_track" : d >= -15 ? "at_risk" : "behind" };
}

const krValue = (v: number, unit: string) => (unit === "gbp" ? `£${Math.round(v).toLocaleString("en-GB")}` : (Math.round(v * 10) / 10).toLocaleString("en-GB"));
const LABEL = { on_track: "on track", at_risk: "at risk", behind: "behind" } as const;

/** The chosen appendices for a pack covering `month`, charts running back `months` months. One with nothing to show is left out. */
export function packAppendices(keys: AppendixKey[], ctx: { model: FinanceModel; reg: RegistersModel; month: string }, months: number): Appendix[] {
  const { model, reg, month } = ctx;
  const person = (id: string | null) => (id ? (reg.people.find((p) => p.id === id)?.name ?? null) : null);
  const window = monthsTo(month, Math.max(1, months));
  const out: Appendix[] = [];
  for (const key of keys) {
    switch (key) {
      case "cash": {
        const input = engineInput(model, "approved");
        if (!input) break;
        const f = forecast(input);
        const weeks = f.weeks.slice(0, model.settings.chartWeeks).map((w) => ({ start: w.start, close: w.close, state: gateState(w.close, model.thresholds) }));
        out.push({
          kind: "cash",
          title: `Cash, ${weeks.length} weeks from ${dayLabel(f.asOf)}`,
          asOf: f.asOf,
          weeks,
          thresholds: { green: model.thresholds.green, amber: model.thresholds.amber, red: model.thresholds.red },
          gateWeeks: model.settings.gateWeeks,
          low: f.low,
          firstOverdrawn: f.firstOverdrawn,
        });
        break;
      }
      case "measures": {
        const defs = reg.measures.filter((d) => d.active).sort((a, b) => Number(b.headline) - Number(a.headline) || a.sortOrder - b.sortOrder);
        const measures = defs
          .map((d) => ({
            id: d.id,
            name: d.name,
            unit: d.unit,
            better: d.better,
            headline: d.headline,
            owner: person(d.ownerId),
            source: d.source === "entered" ? "Typed in" : SOURCE[d.source].from,
            points: measureSeries(d, window, reg.measureValues, reg.measureTargets, reg.measureSettings).filter((p) => p.live),
          }))
          .filter((m) => m.points.length);
        const charted = measures.filter((m) => m.points.some((p) => p.value !== null));
        const noFigures = measures.filter((m) => !charted.includes(m)).map((m) => m.name);
        if (measures.length) out.push({ kind: "measures", title: `Measures, ${full(window.find((m) => defs.some((d) => isLive(d, m))) ?? window[0]!)} to ${full(month)}`, measures: charted, noFigures });
        break;
      }
      case "objectives": {
        if (model.synced.connections.hub.mode === "off") break;
        const items: ObjectiveBar[] = model.synced.objectives.flatMap((so) =>
          so.tactical.map((t) => {
            const pace = paceOf(t.progress, t.periodStart, t.periodEnd, reg.today);
            const kr = t.keyResults[0];
            const hub = t.health.replace(/_/g, " ").toLowerCase();
            return {
              title: t.title,
              owner: t.ownerName,
              periodStart: t.periodStart,
              periodEnd: t.periodEnd,
              progress: t.progress,
              gone: pace.gone,
              status: pace.status,
              keyResult: kr ? `${kr.title}: ${krValue(kr.current, kr.unit)} of ${krValue(kr.target, kr.unit)}` : null,
              hubSays: hub && hub !== LABEL[pace.status] && !(pace.status === "behind" && hub === "behind target") ? hub : null,
            };
          }),
        );
        if (items.length) out.push({ kind: "objectives", title: `Objectives, ${dayLabel(reg.today)}`, items });
        break;
      }
      case "risks": {
        const points: RiskPoint[] = reg.risks
          .filter((r) => r.status === "open")
          .map((r) => {
            const s = score(r.residual);
            return { id: r.id, ref: riskRef(r, reg.riskDomains.find((d) => d.id === r.domainId)), statement: r.statement, residual: r.residual, inherent: r.inherent, score: s, band: bandOf(s, reg.riskScale).band.name, owner: person(r.ownerId) };
          })
          .sort((a, b) => b.score - a.score || a.ref.localeCompare(b.ref, "en", { numeric: true }));
        if (points.length) out.push({ kind: "risks", title: `Risks, ${dayLabel(reg.today)}`, scale: reg.riskScale, points });
        break;
      }
      case "releases": {
        const from = `${window[0]}-01`;
        const items: ChangeBar[] = reg.releases
          .filter((r) => r.status !== "withdrawn" && (RELEASE_OPEN.includes(r.status) || (r.publishedOn ?? "") >= from))
          .sort((a, b) => (a.publishedOn ?? "").localeCompare(b.publishedOn ?? "") || a.ref - b.ref)
          .map((r) => {
            const fw = reg.releaseFrameworks.find((f) => f.id === r.frameworkId);
            const t = releaseTiming(r, fw, reg.today);
            return {
              ref: releaseRef(r.ref),
              what: r.what,
              framework: fw?.name ?? "",
              publishedOn: r.publishedOn,
              toDetect: t.toDetect,
              detectTarget: fw?.detectDays ?? null,
              toDeploy: t.toDeploy,
              deployTarget: fw?.deployDays ?? null,
              open: t.deployOpen,
              overDetect: t.overDetect,
              overDeploy: t.overDeploy,
              unsigned: t.unsigned,
            };
          });
        if (items.length) out.push({ kind: "releases", title: `Statutory changes, ${full(window[0]!)} to ${full(month)}`, items });
        break;
      }
    }
  }
  return out;
}

/** "A", "B"… */
export const appendixLetter = (i: number) => String.fromCharCode(65 + i);
